import { Component, Input, Output, EventEmitter, signal, computed, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContextMenuModule, ContextMenuComponent, MenuItemModel, MenuEventArgs } from '@syncfusion/ej2-angular-navigations';
import { TabSdk } from '@techextensor/tab-sdk';
import { ContextMenuData } from '../../../core/interfaces/dev-tool/json-tree.interface';

@Component({
  selector: 'app-json-tree',
  standalone: true,
  imports: [CommonModule, ContextMenuModule],
  templateUrl: './json-tree.component.html',
  styleUrl: './json-tree.component.scss'
})
export class JsonTreeComponent implements OnInit {
  @Input() set data(value: any) {
    this._data.set(value);
  }
  get data(): any {
    return this._data();
  }

  @Input() set searchQuery(value: string) {
    this._searchQuery.set(value);
  }
  get searchQuery(): string {
    return this._searchQuery();
  }

  @Input() level: number = 0;
  @Input() path: string = '';
  @Input() displayKey: string = '';

  @Output() contextMenuTriggered = new EventEmitter<ContextMenuData>();

  @ViewChild('contextMenu') contextMenu?: ContextMenuComponent;

  private _data = signal<any>(null);
  private _searchQuery = signal<string>('');
  expanded = signal(false);

  isPrimitive = computed(() => {
    const data = this._data();
    return data === null ||
      data === undefined ||
      typeof data === 'string' ||
      typeof data === 'number' ||
      typeof data === 'boolean';
  });

  isArray = computed(() => Array.isArray(this._data()));

  isObject = computed(() => {
    return !this.isPrimitive() && !this.isArray() && typeof this._data() === 'object';
  });

  isEmpty = computed(() => {
    const data = this._data();
    if (this.isArray()) {
      return (data as any[]).length === 0;
    }
    if (this.isObject()) {
      return Object.keys(data || {}).length === 0;
    }
    return false;
  });

  isExpandable = computed(() => {
    return (this.isArray() || this.isObject()) && !this.isEmpty();
  });

  valueClass = computed(() => {
    const data = this._data();
    if (data === null || data === undefined) return 'null';
    if (typeof data === 'string') return 'string';
    if (typeof data === 'number') return 'number';
    if (typeof data === 'boolean') return 'boolean';
    return '';
  });

  formattedValue = computed(() => {
    const data = this._data();
    if (data === null) return 'null';
    if (data === undefined) return 'undefined';
    if (typeof data === 'string') return `"${data}"`;
    return String(data);
  });

  objectKeys = computed(() => Object.keys(this._data() || {}));

  asArray = computed(() => this._data() as any[]);

  private contextMenuTarget: ContextMenuData | null = null;

  menuItems: MenuItemModel[] = [
    {
      text: 'Copy Path',
      iconCss: 'e-icons e-link',
      id: 'copy-path'
    },
    {
      text: 'Copy Key',
      iconCss: 'e-icons e-rename',
      id: 'copy-key'
    },
    {
      text: 'Copy Value',
      iconCss: 'e-icons e-copy',
      id: 'copy-value'
    }
  ];

  ngOnInit() {
    // Auto-expand first level
    if (this.level === 0) {
      this.expanded.set(true);
    }
  }

  hasMatch = computed(() => {
    const searchQuery = this._searchQuery();
    if (!searchQuery || searchQuery.trim().length === 0) {
      return false;
    }
    const query = searchQuery.toLowerCase();
    return this.searchInValue(this._data(), query);
  });

  private searchInValue(value: any, query: string, visited: Set<any> = new Set(), depth: number = 0): boolean {
    if (depth > 50) return false;

    if (value !== null && typeof value === 'object') {
      if (visited.has(value)) return false;
      visited.add(value);
    }

    if (value === null || value === undefined) {
      return 'null'.includes(query) || 'undefined'.includes(query);
    }

    if (typeof value === 'string') {
      return value.toLowerCase().includes(query);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value).toLowerCase().includes(query);
    }

    if (Array.isArray(value)) {
      return value.some((item, index) =>
        String(index).includes(query) || this.searchInValue(item, query, visited, depth + 1)
      );
    }

    if (typeof value === 'object') {
      return Object.keys(value).some(key =>
        key.toLowerCase().includes(query) || this.searchInValue(value[key], query, visited, depth + 1)
      );
    }

    return false;
  }

  filteredArrayItems = computed(() => {
    const arr = this.asArray();
    const searchQuery = this._searchQuery();
    if (!searchQuery || searchQuery.trim().length === 0) {
      return arr.map((value, index) => ({ index, value }));
    }

    const query = searchQuery.toLowerCase();
    return arr
      .map((value, index) => ({ index, value }))
      .filter(item => {
        if (String(item.index).includes(query)) return true;
        return this.searchInValue(item.value, query);
      });
  });

  filteredObjectKeys = computed(() => {
    const keys = this.objectKeys();
    const data = this._data();
    const searchQuery = this._searchQuery();
    if (!searchQuery || searchQuery.trim().length === 0) {
      return keys;
    }

    const query = searchQuery.toLowerCase();
    return keys.filter(key => {
      if (key.toLowerCase().includes(query)) return true;
      return this.searchInValue(data[key], query);
    });
  });

  shouldShowNoResults = computed(() => {
    const searchQuery = this._searchQuery();
    if (!searchQuery || searchQuery.trim().length === 0) {
      return false;
    }

    if (this.isArray()) {
      return this.filteredArrayItems().length === 0;
    }

    if (this.isObject()) {
      return this.filteredObjectKeys().length === 0;
    }

    if (this.isPrimitive()) {
      const query = searchQuery.toLowerCase();
      return !this.searchInValue(this._data(), query);
    }

    return false;
  });

  toggleExpand() {
    this.expanded.update(v => !v);
  }

  // Improved preview - more compact (using computed signals for performance)
  arrayPreview = computed(() => {
    const arr = this.asArray();
    const length = arr.length;
    if (length === 0) return '';

    // Show first few items as preview
    if (length <= 3) {
      return arr.map(v => this.getPreviewString(v)).join(', ');
    }
    return `${this.getPreviewString(arr[0])}, ${this.getPreviewString(arr[1])}, ...`;
  });

  objectPreview = computed(() => {
    const keys = this.objectKeys();
    const length = keys.length;
    if (length === 0) return '';

    const data = this._data();
    // Show first few key-value pairs as preview
    if (length <= 2) {
      return keys.map(k => `${k}: ${this.getPreviewString(data[k])}`).join(', ');
    }
    const firstKey = keys[0];
    return `${firstKey}: ${this.getPreviewString(data[firstKey])}, ...`;
  });

  private getPreviewString(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value.substring(0, 20)}${value.length > 20 ? '...' : ''}"`;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return `[${value.length}]`;
    if (typeof value === 'object') return '{...}';
    return '...';
  }

  buildPath(key?: string): string {
    if (!this.path) {
      return key || '';
    }
    if (!key) {
      return this.path;
    }
    if (!isNaN(Number(key))) {
      return `${this.path}[${key}]`;
    }
    return this.path ? `${this.path}.${key}` : key;
  }

  onItemContextMenu(event: MouseEvent, key: string, value: any): void {
    event.preventDefault();
    event.stopPropagation();

    // Use this.path directly - it already includes the full path to this node
    const path = this.path || key;
    const menuData: ContextMenuData = {
      event,
      type: 'key',
      value,
      key,
      path
    };

    if (this.level === 0) {
      this.contextMenuTarget = menuData;
      if (this.contextMenu) {
        this.contextMenu.open(event.clientY, event.clientX, event.target as HTMLElement);
      }
    } else {
      this.contextMenuTriggered.emit(menuData);
    }
  }

  /**
   * Handle context menu event bubbled from child components
   * If we're root, show the menu. Otherwise, continue bubbling up.
   */
  onChildContextMenu(menuData: ContextMenuData): void {
    if (this.level === 0) {
      // We're at root, handle it
      this.contextMenuTarget = menuData;
      if (this.contextMenu) {
        this.contextMenu.open(
          menuData.event.clientY,
          menuData.event.clientX,
          menuData.event.target as HTMLElement
        );
      }
    } else {
      // Pass it up further
      this.contextMenuTriggered.emit(menuData);
    }
  }

  /**
   * Handle context menu item selection (only happens at root level)
   */
  onContextMenuSelect(args: MenuEventArgs): void {
    if (!this.contextMenuTarget) return;

    const menuId = args.item.id;

    switch (menuId) {
      case 'copy-path':
        TabSdk.ui.copyToClipboard(this.contextMenuTarget.path);
        break;
      case 'copy-key':
        TabSdk.ui.copyToClipboard(this.contextMenuTarget.key || '');
        break;
      case 'copy-value':
        this.copyValueToClipboard(this.contextMenuTarget.value);
        break;
    }

    this.contextMenuTarget = null;
  }

  /**
   * Copy value to clipboard (handles objects/arrays)
   */
  private copyValueToClipboard(value: any): void {
    let textToCopy: string;

    if (typeof value === 'string') {
      textToCopy = value;
    } else if (value === null || value === undefined) {
      textToCopy = String(value);
    } else {
      try {
        textToCopy = JSON.stringify(value, null, 2);
      } catch (err) {
        textToCopy = String(value);
      }
    }

    TabSdk.ui.copyToClipboard(textToCopy);
  }
}